/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import React, { useEffect, useRef, useState } from 'react';
import { NO_DATA_FALLBACK } from '../configuration';
import { ILxsAnonymousDeliveryResources } from '../modules/lxs-anonymous-delivery/lxs-anonymous-delivery.props.autogenerated';
import { IOrderDetailsResources } from '../modules/order-details/order-details.props.autogenerated';
import { ILocalizationState } from '../states/localization-state';
import { dateLocaleFormatter, toLocalDate } from '../utilities';
import { mapEventNameToText } from '../utilities/map-event-name-to-text';

export type ProgressStepStatus = 'completed' | 'active' | 'upcoming';

export interface IProgressBarStep {
    key: string | number;
    eventName: string;
    icon: string;
    status: ProgressStepStatus;
    timestamp?: string;
    location?: string;
}

interface DeliveryProgressBarProps {
    CLASS_NAME: string;
    steps: IProgressBarStep[];
    resources: IOrderDetailsResources | ILxsAnonymousDeliveryResources;
    localizationState: ILocalizationState;
}

const DeliveryProgressBar: React.FC<DeliveryProgressBarProps> = ({ CLASS_NAME, steps, resources, localizationState }) => {
    const sectionRef = useRef<HTMLDivElement | null>(null);
    const firstBubbleRef = useRef<HTMLDivElement | null>(null);
    const activeBubbleRef = useRef<HTMLDivElement | null>(null);
    const lastBubbleRef = useRef<HTMLDivElement | null>(null);
    const [activeLineWidth, setActiveLineWidth] = useState(0);
    const [defaultLineWidth, setDefaultLineWidth] = useState(0);
    const [defaultLineOffset, setDefaultLineOffset] = useState(0);
    const [activeLineHeight, setActiveLineHeight] = useState(0);
    const [defaultLineHeight, setDefaultLineHeight] = useState(0);
    const [defaultLineTopOffset, setDefaultLineTopOffset] = useState(0);

    const allCompleted = steps[steps.length - 1]?.status !== 'upcoming';

    useEffect(() => {
        const updateLinePositions = () => {
            if (firstBubbleRef.current && lastBubbleRef.current) {
                const startOffset = firstBubbleRef.current.offsetLeft;
                const endOffset = lastBubbleRef.current.offsetLeft;
                setDefaultLineOffset(startOffset);
                setDefaultLineWidth(endOffset - startOffset);

                const startTopOffset = firstBubbleRef.current.offsetTop;
                const endTopOffset = lastBubbleRef.current.offsetTop;
                setDefaultLineTopOffset(startTopOffset);
                setDefaultLineHeight(endTopOffset - startTopOffset);

                if (allCompleted) {
                    setActiveLineWidth(endOffset - startOffset);
                    setActiveLineHeight(endTopOffset - startTopOffset);
                } else if (activeBubbleRef.current) {
                    setActiveLineWidth(activeBubbleRef.current.offsetLeft - startOffset);
                    setActiveLineHeight(activeBubbleRef.current.offsetTop - startTopOffset);
                } else {
                    setActiveLineWidth(0);
                    setActiveLineHeight(0);
                }
            }
        };

        updateLinePositions();

        const section = sectionRef.current;
        if (!section || typeof ResizeObserver === 'undefined') {
            return undefined;
        }

        const resizeObserver = new ResizeObserver(updateLinePositions);
        resizeObserver.observe(section);

        return () => resizeObserver.disconnect();
    }, [steps, allCompleted]);

    return (
        <div className={`${CLASS_NAME}__section`} ref={sectionRef}>
            <div
                className={`${CLASS_NAME}__progress-steps__default-line`}
                style={{ width: defaultLineWidth, left: defaultLineOffset + 1 }}
            />
            <div
                className={`${CLASS_NAME}__progress-steps__default-line--vertical`}
                style={{ height: defaultLineHeight, top: defaultLineTopOffset + 1 }}
            />
            {steps.map((step, index) => {
                const isFirst = index === 0;
                const isLast = index === steps.length - 1;
                const isActive = step.status === 'active';
                const ref = isFirst ? firstBubbleRef : isLast ? lastBubbleRef : isActive ? activeBubbleRef : undefined;
                const formattedDate =
                    step.status !== 'upcoming' && step.timestamp
                        ? dateLocaleFormatter(localizationState).formatDate(toLocalDate(step.timestamp))
                        : undefined;

                return (
                    <div className={`${CLASS_NAME}__event`} key={step.key}>
                        <div
                            ref={ref}
                            className={`${CLASS_NAME}__event-bubble ${isActive ? 'active' : ''} ${
                                step.status === 'upcoming' ? 'open' : ''
                            }`}
                        >
                            <span className={`${CLASS_NAME}__event-bubble__icon-${step.icon}`} />
                        </div>
                        <div className={`${CLASS_NAME}__event-description`}>
                            <strong>{step.status !== 'upcoming' ? mapEventNameToText(step.eventName, resources) : NO_DATA_FALLBACK}</strong>
                            <p>{formattedDate || NO_DATA_FALLBACK}</p>
                            <p>{step.location}</p>
                        </div>
                    </div>
                );
            })}
            <div className={`${CLASS_NAME}__progress-steps__filled-line`} style={{ width: activeLineWidth, left: defaultLineOffset + 1 }} />
            <div
                className={`${CLASS_NAME}__progress-steps__filled-line--vertical`}
                style={{ height: activeLineHeight, top: defaultLineTopOffset + 1 }}
            />
        </div>
    );
};

export default DeliveryProgressBar;
